import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { FaChrome } from "react-icons/fa";
import { CRE_EXTENSION_STORE_URL } from "../../../constants/creExtension";

/**
 * CRE floating dictation — install guidance (layout aligned with Zikaron Video Recording extension block).
 */
export default function CreExtensionHelpModal({
  isOpen,
  onClose,
  /** Radical layout: modal sits between top and vertical center */
  alignBetweenTopAndCenter = false,
}) {
  const { t } = useTranslation();
  const [showSteps, setShowSteps] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) setShowSteps(false);
  }, [isOpen]);

  const openInstallPage = useCallback(() => {
    window.open(CRE_EXTENSION_STORE_URL, "_blank", "noopener,noreferrer");
    setShowSteps(true);
  }, []);

  if (!isOpen) return null;

  const isEdgeBrowser =
    typeof navigator !== "undefined" &&
    (navigator.userAgent.includes("Edg") ||
      navigator.userAgent.includes("Edge"));
  const installStep2 = isEdgeBrowser
    ? t("creFloatingDictationModalStep2Edge")
    : t("creFloatingDictationModalStep2Chrome");

  const overlayClassName = alignBetweenTopAndCenter
    ? "fixed inset-0 z-[1100] flex justify-center items-start pt-[min(22vh,10rem)] sm:pt-[min(26vh,12rem)] bg-black/50 p-4"
    : "fixed inset-0 z-[1100] flex items-center justify-center bg-black/50 p-4";

  return (
    <div
      className={overlayClassName}
      role="presentation"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="cre-extension-help-title"
        className="max-w-[min(36rem,calc(100vw-2rem))] w-full min-w-0 max-h-[min(90vh,40rem)] overflow-y-auto overscroll-contain rounded-xl bg-white dark:bg-gray-800 shadow-xl border border-gray-200 dark:border-gray-600 p-5 sm:p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h2
          id="cre-extension-help-title"
          className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white mb-4"
        >
          {t("creFloatingDictationModalTitle")}
        </h2>

        <div className="mb-5 p-4 sm:p-5 bg-gray-50 dark:bg-[#111827] rounded-lg border-2 border-gray-200 dark:border-gray-700 shadow-sm min-w-0">
          <div className="flex flex-col gap-4 min-w-0">
            <div className="flex items-start gap-3 min-w-0">
              <FaChrome
                className="text-gray-600 dark:text-white text-2xl shrink-0 mt-0.5"
                aria-hidden
              />
              <div className="min-w-0 flex-1 space-y-2">
                <h3 className="font-semibold text-gray-900 dark:text-white text-base leading-snug">
                  {t("creFloatingDictationModalExtensionTitle")}
                </h3>
                <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                  {t("creFloatingDictationModalExtensionDescription")}
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                  {t("creFloatingDictationModalExtensionHint")}
                </p>
              </div>
            </div>

            <div className="flex flex-col gap-2 w-full min-w-0 sm:flex-row sm:flex-wrap sm:justify-end">
              <button
                type="button"
                onClick={openInstallPage}
                className="w-full sm:w-auto sm:min-w-[10rem] justify-center px-4 py-2.5 bg-[#A4C639] dark:bg-[#A4C639] text-white dark:text-white rounded-lg hover:bg-[#CBEA7B80] dark:hover:bg-[#CBEA7B80] transition-colors text-sm font-medium"
              >
                {t("creFloatingDictationModalInstallBtn")}
              </button>
              <button
                type="button"
                onClick={() => setShowSteps((v) => !v)}
                className="w-full sm:w-auto sm:min-w-[10rem] justify-center px-4 py-2.5 rounded-lg bg-gray-200 hover:bg-gray-300 text-gray-900 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-white transition-colors text-sm font-medium"
              >
                {showSteps
                  ? t("creFloatingDictationModalHideStepsBtn")
                  : t("creFloatingDictationModalShowStepsBtn")}
              </button>
            </div>
          </div>

          {showSteps && (
            <div className="mt-4 p-3 sm:p-4 bg-gray-100 dark:bg-gray-800 rounded border border-gray-300 dark:border-gray-600 min-w-0">
              <h4 className="font-medium text-gray-900 dark:text-white mb-2 text-sm sm:text-base">
                {t("creFloatingDictationModalInstallGuideTitle")}
              </h4>
              <ol className="list-decimal list-outside pl-5 sm:pl-6 text-sm text-gray-800 dark:text-gray-300 space-y-2 leading-relaxed">
                <li className="pl-1">{t("creFloatingDictationModalGuideStep1")}</li>
                <li className="pl-1">{installStep2}</li>
                <li className="pl-1">{t("creFloatingDictationModalGuideStep3")}</li>
                <li className="pl-1">{t("creFloatingDictationModalGuideStep4")}</li>
              </ol>
              <p className="mt-3 text-xs text-gray-700 dark:text-gray-400 leading-relaxed">
                {t("creFloatingDictationModalInstallFeatures")}
              </p>
            </div>
          )}
        </div>

        <div className="flex justify-stretch sm:justify-end">
          <button
            type="button"
            className="w-full sm:w-auto rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-4 py-2.5 text-sm font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
            onClick={onClose}
          >
            {t("creFloatingDictationModalClose")}
          </button>
        </div>
      </div>
    </div>
  );
}
